/**
 * The request shape every `/api` list caller shares, and the `limit → size`
 * rename that makes it the kernel's query.
 *
 * The whitelist vocabulary and its three pure predicates moved into
 * `@resource-kit/controllers` (#2167, ADR-0035 D1) and #2170 pointed their
 * callers at the package. What stayed is what is genuinely fastcade's:
 * `toApiListParams` speaks THIS app's ADR-0006 wire contract.
 */
import { isWhitelisted, type FilterWhitelist } from '@resource-kit/controllers';

/**
 * The request-param shape every `/api` list caller shares: the table state
 * produced by `useDataTableParams` (`page`/`limit`/`sort`/`search`) plus
 * optional pre-serialized filter `extraParams`. Every field is optional so
 * `DataTableParams` (whose page/limit/sort are required) assigns cleanly.
 */
export interface ListRequestParams {
  page?: number;
  limit?: number;
  sort?: string;
  search?: string;
  extraParams?: Record<string, string>;
}

/**
 * Translate the shared list-request params into the query the backend list
 * kernel (`platform/http/list-query`) accepts. Bridges two divergences from
 * the retired list face this exists for:
 *
 *   1. The kernel's page-size reserved key is `size`, not `limit`. A stray
 *      `limit` is parsed as an unknown filter field and 400s the whole
 *      request (the #935 P0), so `limit` is emitted as `size`.
 *   2. Filters (`extraParams`, already in the kernel `<field>=<op>:<value>`
 *      form from `serializeConditionsToParams`) are whitelisted against the
 *      resource's `filterable` config; a field or op the backend does not
 *      accept 400s the whole page, so it is dropped — an unsupported filter
 *      degrades to a looser list rather than an error (prefer the list
 *      loads). With no whitelist, all filters are dropped for the same
 *      reason. Reserved keys smuggled into `extraParams` (`page`/`sort`/…)
 *      are likewise not in any whitelist, so they never override the
 *      dedicated top-level params.
 *
 * `sort` passes through verbatim — sort-field whitelisting is the kernel's
 * concern, out of scope here; blank `search` is omitted.
 */
export function toApiListParams(
  p: ListRequestParams,
  filterable?: FilterWhitelist,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (p.page != null) out.page = p.page;
  if (p.limit != null) out.size = p.limit;
  if (p.sort) out.sort = p.sort;
  if (p.search != null && p.search.trim() !== '') out.search = p.search;
  if (p.extraParams && filterable) {
    for (const [field, value] of Object.entries(p.extraParams)) {
      if (isWhitelisted(field, value, filterable)) out[field] = value;
    }
  }
  return out;
}
